/* eslint-disable react/no-this-in-sfc */
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { SELECTED_ISSUERS_ATOM } from "components/charts/modern/overlay/IssuersSelect/atoms";
import Avatar from "components/core/Avatar";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import { AnimatePresence, motion } from "framer-motion";
import { useAtom } from "jotai";
import { XIcon } from "lucide-react";
import { useIntl } from "react-intl";

interface SelectedIssuersStackProps {
  isLoading?: boolean;
  canRemove?: boolean;
}

export default function SelectedIssuersStack({ isLoading, canRemove = true }: SelectedIssuersStackProps) {
  const intl = useIntl();
  const [selectedIssuers, setSelectedIssuers] = useAtom(SELECTED_ISSUERS_ATOM);

  if (selectedIssuers.length === 0) {
    return null;
  }

  return (
    <ToggleGroup.Root
      loop={true}
      type="multiple"
      disabled={isLoading}
      className={classNames(
        "grid w-full grid-flow-col rounded-xl border border-transparent bg-slate-50 p-1 dark:bg-slate-950",
        "ring-1 ring-slate-200 ring-offset-2 ring-offset-white focus-visible:outline-none",
        "sm:flex sm:w-auto sm:max-w-xs sm:justify-normal sm:space-x-2",
      )}
      orientation="horizontal"
      value={selectedIssuers.map((issuer) => issuer.tickerSymbol)}
      onValueChange={(tickerSymbols) => {
        setSelectedIssuers(
          selectedIssuers.filter((issuer) => {
            return tickerSymbols.includes(issuer.tickerSymbol);
          }),
        );
      }}
    >
      <AnimatePresence>
        {selectedIssuers.map((issuer) => {
          return (
            <ToggleGroup.Item
              key={issuer._id}
              value={issuer.tickerSymbol}
              className={classNames(
                "relative z-0 h-9 w-full items-center justify-center",
                "border border-transparent",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-1 focus-visible:ring-offset-gray-300",
                "data-[state=on]:focus-visible:rounded-xl data-[state=on]:focus-visible:bg-gray-50 data-[state=on]:focus-visible:text-gray-600",
              )}
            >
              <motion.div
                key={issuer._id}
                initial={{
                  scale: 0,
                }}
                animate={{
                  scale: 1,
                }}
                exit={{
                  scale: 0,
                }}
                className="relative inline-flex"
              >
                <SimpleTextTooltip content={isLoading ? `${issuer.name} (...)` : issuer.name}>
                  <Avatar
                    src={issuer.niceLogo?.url || null}
                    hashValue={issuer._id}
                    alt={issuer.name}
                    size={36}
                    squircle={true}
                    className={classNames("md:inline-flex", {
                      "opacity-10": isLoading,
                    })}
                  />
                </SimpleTextTooltip>
                {canRemove && !isLoading && (
                  <span
                    aria-label={intl.formatMessage({ defaultMessage: "Remover", id: "TolqfU" })}
                    className={classNames(
                      "absolute -right-1 -top-1 z-10 rounded-lg p-0.5",
                      "pointer-events-none border-transparent bg-rose-600 text-white",
                    )}
                  >
                    <XIcon strokeWidth={3} className="h-3 w-3" />
                  </span>
                )}
                {isLoading && (
                  <span
                    aria-label={intl.formatMessage({ defaultMessage: "Carregando...", id: "EMLr/e" })}
                    className={classNames(
                      "pointer-events-none absolute inset-0 inline-flex h-full w-full animate-pulse",
                      "items-center justify-center rounded-full bg-slate-50 squircle",
                    )}
                  />
                )}
              </motion.div>
            </ToggleGroup.Item>
          );
        })}
      </AnimatePresence>
    </ToggleGroup.Root>
  );
}
